/**
 * ColyseusTransport — adapts the existing GameClient to the GameTransport interface.
 *
 * Keeps the legacy Colyseus server path working while the edge path is built.
 * This is a thin adapter over the existing colyseus.js client.
 */

import type { GameTransport, ServerMessageHandler, TransportClientMessage } from './Transport.js';
import { GameClient } from './Client.js';

export class ColyseusTransport implements GameTransport {
  private client: GameClient | null = null;
  private handlerUnsubscribes: Array<() => void> = [];
  private url: string;

  constructor(url: string) {
    this.url = url;
  }

  get sessionId(): string | null {
    return this.client?.sessionId ?? null;
  }

  get isConnected(): boolean {
    return (this.client?.isInRoom) ?? false;
  }

  async connect(): Promise<void> {
    this.client = new GameClient(this.url);
    await this.client.connect();
  }

  async joinRoom(roomName: string, options?: Record<string, unknown>): Promise<void> {
    if (this.client === null) {
      throw new Error('Not connected. Call connect() first.');
    }
    await this.client.joinRoom(roomName, options);
  }

  async leaveRoom(): Promise<void> {
    if (this.client !== null) {
      await this.client.leaveRoom();
    }
  }

  send(message: TransportClientMessage): void {
    // Preserve clientSeq through the legacy Colyseus transport
    this.client?.send({ type: message.type, payload: message.payload, clientSeq: message.clientSeq } as { type: string; payload: unknown; clientSeq?: number });
  }

  onMessage(handler: ServerMessageHandler): () => void {
    if (this.client === null) {
      throw new Error('Not connected. Call connect() before registering handlers.');
    }
    const unsub = this.client.onMessage(handler);
    const wrapped = (): void => {
      unsub();
      const idx = this.handlerUnsubscribes.indexOf(wrapped);
      if (idx !== -1) {
        this.handlerUnsubscribes.splice(idx, 1);
      }
    };
    this.handlerUnsubscribes.push(wrapped);
    return wrapped;
  }

  async disconnect(): Promise<void> {
    // Unsubscribe all handlers
    for (const unsub of this.handlerUnsubscribes) {
      unsub();
    }
    this.handlerUnsubscribes = [];

    if (this.client !== null) {
      await this.client.disconnect();
      this.client = null;
    }
  }


}
